import { useMemo, useState } from "react";
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Icon,
  InputText,
  type InputTextProps,
  Stack,
  Text,
  Title,
} from "@bookingcom/bui-react";
import { MagnifyingGlassIcon } from "@bookingcom/bui-assets-react/streamline";
import type { Environment, Feature } from "../../../types";
import { isValidFeature } from "../../../utils/features";
import styles from "./FeatureOverrideSheet.module.css";

export type FeatureOverridePickerProps = {
  features: Environment["features"];
  overriddenFeatures: Feature[];
  onAdd: (feature: Feature, enabled: boolean) => void;
};

export const FeatureOverridePicker = ({
  features,
  overriddenFeatures,
  onAdd,
}: FeatureOverridePickerProps) => {
  const [query, setQuery] = useState("");
  const [pendingFeature, setPendingFeature] = useState<Feature>();

  const availableFeatures = useMemo(
    () =>
      Object.keys(features)
        .filter(isValidFeature)
        .filter((feature) => !overriddenFeatures.includes(feature))
        .sort((featureA, featureB) => featureA.localeCompare(featureB)),
    [features, overriddenFeatures]
  );

  const matchingFeatures = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (normalizedQuery.length <= 2) return [];

    return availableFeatures.filter((feature) => feature.toLowerCase().includes(normalizedQuery));
  }, [availableFeatures, query]);

  const handleSearchInput = (value: string) => {
    setQuery(value);
    if (value !== pendingFeature) {
      setPendingFeature(undefined);
    }
  };

  const handleAutocompleteChange = (value: string) => {
    setQuery(value);
    if (availableFeatures.includes(value as Feature)) {
      setPendingFeature(value as Feature);
    }
  };

  const handleAdd = (enabled: boolean) => {
    if (!pendingFeature) return;

    onAdd(pendingFeature, enabled);
    setPendingFeature(undefined);
    setQuery("");
  };

  return (
    <>
      <Autocomplete
        onInput={({ value }) => handleSearchInput(value)}
        onChange={({ value }) => handleAutocompleteChange(value)}
      >
        <Autocomplete.Trigger>
          {(inputAttributes, positionRef) => (
            <InputText
              name="featureFlagSearch"
              label="Search feature flags"
              placeholder="Enter at least 3 characters"
              value={query}
              startSlot={<Icon svg={MagnifyingGlassIcon} size="small" />}
              clearButtonVisibility="on-edit"
              clearButtonAriaLabel="Clear feature flag search"
              attributes={{ ref: positionRef }}
              inputAttributes={inputAttributes as unknown as InputTextProps["inputAttributes"]}
            />
          )}
        </Autocomplete.Trigger>
        <Autocomplete.Popover>
          {matchingFeatures.map((feature) => {
            const isRunning = Boolean(features[feature]);
            const status = isRunning ? "On" : "Off";

            return (
              <Autocomplete.Item
                key={feature}
                value={feature}
                attributes={{ "aria-label": `${feature}, ${status}` }}
              >
                <Stack
                  className={styles.featureAutocompleteItem}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  wrap="nowrap"
                  gap={3}
                >
                  <Text className={styles.featureFlagName} attributes={{ title: feature }}>
                    {feature}
                  </Text>
                  <Badge
                    className={styles.featureStatusBadge}
                    variant={isRunning ? "constructive" : "outline"}
                    text={status}
                  />
                </Stack>
              </Autocomplete.Item>
            );
          })}
          {query.trim().length > 2 && matchingFeatures.length === 0 && (
            <Box mixin={{ padding: 3 }}>
              <Text variant="body_2" color="neutral_alt">
                No matching feature flags
              </Text>
            </Box>
          )}
        </Autocomplete.Popover>
      </Autocomplete>

      {pendingFeature && (
        <Stack
          className={styles.pendingFeatureRow}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          gap={3}
        >
          <Title
            className={styles.pendingFeatureDetails}
            title={pendingFeature}
            titleClassName={styles.pendingFeatureName}
            titleAttributes={{ title: pendingFeature }}
            subtitle="Choose the initial override value"
          />
          <Stack
            className={styles.pendingFeatureActions}
            direction="row"
            justifyContent="end"
            wrap="nowrap"
          >
            <Button variant="secondary" onClick={() => handleAdd(true)}>
              Enable
            </Button>
            <Button variant="secondary-neutral" onClick={() => handleAdd(false)}>
              Disable
            </Button>
          </Stack>
        </Stack>
      )}
    </>
  );
};
